export const sources = ["bluesky", "reddit", "mastodon", "lemmy"] as const;
export const categories = [
  "political",
  "advertisement",
  "news",
  "entertainment",
  "personal",
  "educational",
  "motivational",
  "other",
  "unavailable",
] as const;
export const confidences = ["low", "medium", "high"] as const;
export type Source = (typeof sources)[number];
export type Category = (typeof categories)[number];
export type Confidence = (typeof confidences)[number];
export type Label = "ai_generated" | "ai_edited";
export type Post = {
  key: string;
  event_id: string;
  source: Source;
  url: string;
  text: string;
  published_at: string;
  detected_at: string;
  expires_at_unix: number;
  label: Label;
  confidence: Confidence;
  category: Category;
};
export type SourceStatus = {
  state: "running" | "paused" | "disconnected" | "unavailable";
  reason: string;
  observed_at?: string;
  counter_started_at?: string;
  detection_min_confidence?: Confidence;
  counters: Record<string, number>;
};
export type Feed = {
  epoch: string;
  sequence: number;
  posts: Post[];
  sources: Partial<Record<Source, SourceStatus>>;
  offset: number;
  arrivalRate?: { count: number; seconds: number };
};
export type Filters = {
  source: Source | "all";
  category: Category | "all";
  label: Label | "all";
  confidence: Confidence;
};
export const emptyFeed = (): Feed => ({
  epoch: "",
  sequence: -1,
  posts: [],
  sources: {},
  offset: 0,
});
export const defaultFilters: Filters = {
  source: "all",
  category: "all",
  label: "all",
  confidence: "low",
};

const object = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new Error("Invalid feed event");
  return value as Record<string, unknown>;
};
const date = (value: unknown): string => {
  if (
    typeof value !== "string" ||
    value.length > 40 ||
    !Number.isFinite(Date.parse(value))
  )
    throw new Error("Invalid timestamp");
  return value;
};
export function parsePost(value: unknown): Post {
  const p = object(value);
  if (
    typeof p.key !== "string" ||
    p.key.length > 2100 ||
    typeof p.event_id !== "string" ||
    !/^[a-f0-9]{64}$/.test(p.event_id) ||
    !sources.includes(p.source as Source) ||
    typeof p.text !== "string" ||
    [...p.text].length > 10000 ||
    typeof p.url !== "string" ||
    p.url.length > 2048 ||
    /[\s\\]/u.test(p.url) ||
    !["ai_generated", "ai_edited"].includes(String(p.label)) ||
    !confidences.includes(p.confidence as Confidence) ||
    !categories.includes(p.category as Category) ||
    typeof p.expires_at_unix !== "number" ||
    !Number.isFinite(p.expires_at_unix)
  )
    throw new Error("Invalid post");
  const url = new URL(p.url);
  if (
    url.protocol !== "https:" ||
    url.username ||
    url.password ||
    url.hash ||
    url.port ||
    !url.hostname.includes(".") ||
    (p.source === "bluesky" &&
      (url.hostname !== "bsky.app" || !url.pathname.startsWith("/profile/"))) ||
    (p.source === "reddit" &&
      (!["reddit.com", "www.reddit.com"].includes(url.hostname) ||
        !url.pathname.includes("/comments/")))
  )
    throw new Error("Invalid source link");
  return {
    key: p.key,
    event_id: p.event_id,
    source: p.source as Source,
    url: p.url,
    text: p.text,
    published_at: date(p.published_at),
    detected_at: date(p.detected_at),
    expires_at_unix: p.expires_at_unix,
    label: p.label as Label,
    confidence: p.confidence as Confidence,
    category: p.category as Category,
  };
}
const post = parsePost;
function statuses(value: unknown): Feed["sources"] {
  const input = object(value);
  const result: Feed["sources"] = {};
  for (const source of sources) {
    if (!input[source]) continue;
    const s = object(input[source]);
    if (
      !["running", "paused", "disconnected", "unavailable"].includes(
        String(s.state),
      ) ||
      typeof s.reason !== "string" ||
      s.reason.length > 80
    )
      throw new Error("Invalid status");
    const counts = object(s.counters);
    const counters: Record<string, number> = {};
    for (const key of [
      "scored",
      "detected",
      "published",
      "withheld",
      "window_scanned",
      "window_flagged",
    ]) {
      const n = counts[key];
      if (Number.isSafeInteger(n) && Number(n) >= 0) counters[key] = Number(n);
    }
    result[source] = {
      state: s.state as SourceStatus["state"],
      reason: s.reason,
      counters,
      ...(s.observed_at ? { observed_at: date(s.observed_at) } : {}),
      ...(s.counter_started_at
        ? { counter_started_at: date(s.counter_started_at) }
        : {}),
      ...(confidences.includes(s.detection_min_confidence as Confidence)
        ? { detection_min_confidence: s.detection_min_confidence as Confidence }
        : {}),
    };
  }
  return result;
}
export function prune(feed: Feed, now: number): Feed {
  let bytes = 0;
  const posts = feed.posts
    .filter((p) => {
      if (p.expires_at_unix * 1000 <= now + feed.offset) return false;
      // Four bytes per UTF-16 code unit is a conservative bound for UTF-8 text.
      bytes += (p.text.length + p.key.length + p.url.length + 512) * 4;
      return bytes <= 2 * 1024 * 1024;
    })
    .slice(0, 200);
  return { ...feed, posts };
}
export function receive(
  feed: Feed,
  kind: string,
  value: unknown,
  id: string,
  now: number,
): Feed {
  const data = object(value);
  if (kind === "heartbeat")
    return prune(
      {
        ...feed,
        sources: statuses(data.sources),
        offset: Date.parse(date(data.server_time)) - now,
        arrivalRate: parseRate(data.arrival_rate),
      },
      now,
    );
  const parts = /^([a-f0-9]{32}):(\d{1,16})$/.exec(id);
  if (!parts || !Number.isSafeInteger(Number(parts[2])))
    throw new Error("Invalid stream cursor");
  const [, epoch, seq] = parts;
  const sequence = Number(seq);
  if (kind === "reset") {
    if (
      data.epoch !== epoch ||
      data.sequence !== sequence ||
      !Array.isArray(data.posts) ||
      data.posts.length > 200
    )
      throw new Error("Invalid snapshot");
    const posts = data.posts.map(post);
    if (new Set(posts.map((p) => p.key)).size !== posts.length)
      throw new Error("Duplicate snapshot post");
    return prune(
      {
        epoch,
        sequence,
        posts,
        sources: statuses(data.sources),
        offset: Date.parse(date(data.server_time)) - now,
        arrivalRate: parseRate(data.arrival_rate),
      },
      now,
    );
  }
  if (epoch !== feed.epoch) throw new Error("Stream requires reset");
  if (sequence <= feed.sequence) return prune(feed, now);
  let next = { ...feed, sequence };
  if (kind === "post") {
    const incoming = post(data);
    const index = feed.posts.findIndex((p) => p.key === incoming.key);
    const posts = [...feed.posts];
    if (index < 0) posts.unshift(incoming);
    else posts[index] = incoming;
    next = {
      ...next,
      posts,
      arrivalRate: parseRate(data.arrival_rate) ?? feed.arrivalRate,
    };
  } else if (kind === "remove")
    next.posts = feed.posts.filter((p) => p.key !== data.key);
  else if (kind === "status") next.sources = statuses(data);
  else throw new Error("Unknown feed event");
  return prune(next, now);
}
export function matches(p: Post, f: Filters) {
  return (
    (f.source === "all" || f.source === p.source) &&
    (f.category === "all" || f.category === p.category) &&
    (f.label === "all" || f.label === p.label) &&
    confidences.indexOf(p.confidence) >= confidences.indexOf(f.confidence)
  );
}

function parseRate(value: unknown): Feed["arrivalRate"] {
  if (!value || typeof value !== "object") return undefined;
  const rate = value as Record<string, unknown>;
  if (
    !Number.isSafeInteger(rate.count) ||
    Number(rate.count) < 0 ||
    typeof rate.seconds !== "number" ||
    !Number.isFinite(rate.seconds) ||
    rate.seconds < 1 ||
    rate.seconds > 900
  )
    return undefined;
  return { count: Number(rate.count), seconds: rate.seconds };
}
